import React, { useEffect, useMemo, useRef, useState } from "react";
import { ConfirmationModal, Spinner, TitlePage } from "../../shared";
import { FormattedMessage, useIntl } from "react-intl";
import functions from "../../../utils/functionsFirebase";
import FileTable from "./fileTable/fileTable";
import { useDispatch, useSelector } from "react-redux";
import drive from "../../drive";
import { toast } from "react-toastify";
import './myDrive.css'
import RecycleBin from "../recycleBin/recycleBin";
import { isMobileDevice, isSmallScreen, isMediumScreen } from "../../../utils/utils";
import UploadUtils from "../uploadUtils/uploadUtils";
import { Helmet } from "react-helmet";
import icon from "../../../images/pageIcon.png";
import users from "../../users";
import useDownloadStore from "../../../store/downloadStore";
import CustomModalV2 from "../../shared/custom-modal-v2/custom-modal-v2";
import FileViewer from "../fileViewer/fileViewer";
import ShareFileForm from "./shareFileForm/shareFileForm";
import ViewerSquadflixMenu from "./viewerSquadflixMenu/viewerSquadflixMenu";
import { useLocation, useNavigate } from "react-router-dom";
import { ogFunctions } from "../../../apis/og-functions/ogFunctionsApi";
import FileSearcher from "../fileSearcher/fileSearcher";
import Cap17driveHeader from "../../cap17drive/cap17driveIndex/cap17driveHeader/cap17driveHeader";
import UseScrollContainerRef from "../../cap17drive/movies/utils/useScrollContainerRef";

function MyDrive({ toggleContextualMenu, draggingItems }) {

    const intl = useIntl();
    const dispatch = useDispatch();
    const { addFileToQueue } = useDownloadStore(state => state.actions);
    const parentRef = UseScrollContainerRef();

    const [updatedFiles, setUpdatedFiles] = useState(false);
    const [createFolderOpen, setCreateFolderOpen] = useState(false);
    const [loadingNewFolder, setLoadingNewFolder] = useState(false);
    const [activeFile, setActiveFile] = useState(null);
    const uploadUtils = useRef(null);
    const [searching, setSearching] = useState(false);
    const [validGroups, setValidGroups] = useState(null);
    const fileViewerAccordion = useRef(null);
    const buttonShareForm = useRef(null);
    const buttonShowFileViewer = useRef(null);
    const buttonShowSquadflixForm = useRef(null);
    const currentShowingOptions = useRef(0);
    const [fileViewerOpen, setFileViewerOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const folder = useSelector(drive.selectors.getCurrentFolder);
    const currentFolderId = useSelector(drive.selectors.getCurrentFolderId);
    const folderTree = useSelector(drive.selectors.getFolderTree)
    const darkMode = useSelector(users.selectors.isDarkMode);

    const folderRoute = useMemo(() => {
        let route = location.pathname.replace("/my-drive", "");
        if (route.includes("/file/")) {
            route = route.split("/file/")[0];
        }
        return route;
    }, [location.pathname]);

    const fileId = useMemo(() => {
        const parts = location.pathname.split("/file/");
        return parts.length > 1 ? parts[1] : null;
    }, [location.pathname]);

    const paths = useMemo(() => {
        let cleanPath = location.pathname;
        if (cleanPath.includes("/file/")) {
            cleanPath = cleanPath.split("/file/")[0];
        }
        return cleanPath.split("/").filter(Boolean);
    }, [location.pathname]);


    useEffect(() => {
        async function getFiles() {
            setUpdatedFiles(true);
            const getFolder = ogFunctions.httpsCallable('getFolderByRoute');
            const result = await getFolder({ route: folderRoute });
            dispatch(drive.actions.treeFolderAdd(result.data));
            dispatch(drive.actions.changeCurrentFolder(result.data));
        }

        dispatch(drive.actions.changeCurrentFolder({}))
        getFiles();
    }, [folderRoute]);

    useEffect(() => {
        if (fileId && folder?.files) {
            const file = folder.files.find(f => f.id === fileId);
            if (file) {
                setActiveFile(file);
                setFileViewerOpen(true);
            }
        } else if (!fileId) {
            setActiveFile(null);
            setFileViewerOpen(false);
        }
    }, [fileId, folder?.files]);

    async function handleCloseCreateFolder(confirm, folderName) {
        if (confirm) {
            if (folderName.length < 3) {
                toast.error(intl.formatMessage({ id: "drive.create-folder.error-name" }))
                return;
            }
            setLoadingNewFolder(true);
            const createFolder = functions.httpsCallable('createFolder');
            const result = await createFolder({
                folder: {
                    name: folderName,
                    folderId: currentFolderId
                }
            });
            dispatch(drive.actions.folderCreated(result.data.folder));

            toast.success(intl.formatMessage({ id: "drive.create-folder.success" }))
            setCreateFolderOpen(false);
            setLoadingNewFolder(false);
        } else {
            setCreateFolderOpen(false);
        }
    }

    async function handleGoBack() {
        let cleanPath = location.pathname;
        if (cleanPath.includes("/file/")) {
            cleanPath = cleanPath.split("/file/")[0];
        }
        const path = cleanPath.split("/").filter(Boolean);
        path.pop();
        navigate(`/${path.join("/")}`, { replace: true });
    }

    function handleShowOptions(option, canClose) {
        if (option === currentShowingOptions.current) {
            if (canClose) {
                handleShowOptions(0);
            }
            return false;
        }

        switch (option) {
            case 0:
                buttonShowFileViewer.current.click();
                break;
            case 1:
                buttonShareForm.current.click();
                break;
            case 2:
                buttonShowSquadflixForm.current.click();
                break;
        }

        currentShowingOptions.current = option;

        return true;

    }

    const rightClassSize = isSmallScreen() ? 6 : (isMediumScreen() ? 4 : 3);

    async function handleOpenFile(file) {
        let basePath = location.pathname;
        if (basePath.includes("/file/")) {
            basePath = basePath.split("/file/")[0];
        }
        navigate(`${basePath}/file/${file.id}`, { replace: true });
        currentShowingOptions.current = 0;
    }

    function openSquadflixForm() {
        handleShowOptions(2, true);
    }

    async function getValidGroups() {
        const opened = handleShowOptions(1, true);
        if (!opened) {
            return;
        }
        const getValidGroupsToShare = functions.httpsCallable('getValidGroupsToShare');
        const res = await getValidGroupsToShare();
        setValidGroups(res.data.groups);
    }

    async function generateShareUrl(file) {
        handleShowOptions(0);
        if (file.shareUrl) {
            return;
        }
        const generateFileShareUrl = functions.httpsCallable('generateFileShareUrl');
        const res = await generateFileShareUrl({ fileId: file.id });
        folder.files.find(x => x.id === file.id).shareUrl = res.data.file.shareUrl;
        setActiveFile(res.data.file);
    }

    const handleChangeFolder = (folder) => {
        let cleanPath = location.pathname;
        if (cleanPath.includes("/file/")) {
            cleanPath = cleanPath.split("/file/")[0];
        }
        const path = cleanPath.split("/").filter(Boolean);
        path.push(folder.folderName.replaceAll("/", "-"));
        navigate(`/${path.join("/")}`, { replace: true });
    }

    return (
        <div className="d-flex d-column justify-content-center flex-wrap myDrive__container">
            <Helmet>
                <meta charSet="utf-8" />
                <title>SquadDisk | {intl.formatMessage({ id: "app.my-drive" })}</title>
                <link rel="icon" href={icon} />
            </Helmet>
            <RecycleBin />
            <div style={{
                position: "fixed",
                zIndex: 2,
                top: "0",
                width: parentRef.width,
                height: 109,
                backgroundColor: "rgba(33, 37, 41, 1)"
            }}>

            </div>
            {(!searching || !isMobileDevice()) && (
                <div style={{
                    position: "sticky",
                    width: "100%",
                    top: 2,
                    zIndex: 3,
                }}>
                    <TitlePage
                        disableScroll
                        leftClass="col-12"
                        title={<div
                            className="d-flex flex-row justify-content-center align-items-center w-100 cap17drive-header__title fs-4">
                            <div className="d-flex flex-row align-items-center gap-4">
                                <div onClick={() => navigate('/my-drive', { replace: true })} style={{
                                    cursor: "pointer"
                                }}>
                                    <i className="fa-solid fa-solid fa-hard-drive"></i>&nbsp; Mi Drive
                                </div>
                            </div>
                        </div>} icon={undefined} rightOptions={undefined} titleClass={undefined} rightClass={undefined}
                        hideSeparator={true} fixedHeight={undefined} style={undefined} />
                </div>
            )}
            <div style={{
                position: "sticky",
                top: 30,
                width: "100%",
                zIndex: 3,
                borderBottom: "1px solid " + (darkMode ? "#343a40" : "#dee2e6"),
                paddingBottom: "10px",
                marginBottom: "20px",
            }}>
                <div className="myDrive-header-row">
                    {/* Left: Actions & Go Back */}
                    <div className="myDrive-header-back">
                        {paths.length > 1 ? (
                            <button className="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center p-0 myDrive-header-btn"
                                onClick={handleGoBack}
                                title="Atrás">
                                <i className="fa-solid fa-arrow-left"></i>
                            </button>
                        ) : (
                            isMobileDevice() && (
                                <button className="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center p-0 myDrive-header-btn"
                                    data-bs-toggle="modal" data-bs-target="#recycleBinModal"
                                    title={intl.formatMessage({ id: "drive.recycle-bin" })}>
                                    <i className="fa-solid fa-trash"></i>
                                </button>
                            )
                        )}

                        {isMobileDevice() && (
                            <button className="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center p-0 myDrive-header-btn"
                                data-bs-toggle="modal" data-bs-target="#newFolderModal"
                                onClick={() => setCreateFolderOpen(true)}
                                title={intl.formatMessage({ id: "drive.create-folder" })}>
                                <i className="fa-solid fa-folder-plus"></i>
                            </button>
                        )}
                    </div>

                    {/* Right: Actions */}
                    <div className="myDrive-header-actions">
                        <div className="d-flex flex-row align-items-center position-relative">
                            {searching ? (
                                <div className="d-flex align-items-center" style={{ width: isMobileDevice() ? "140px" : "240px", transition: "all 0.3s ease" }}>
                                    <FileSearcher onOpenFile={handleOpenFile} searching={searching} setSearching={setSearching}
                                        title={intl.formatMessage({ id: "app.my-drive" })} />
                                </div>
                            ) : (
                                <button className="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center p-0 myDrive-header-btn"
                                    onClick={() => setSearching(true)}
                                    title="Buscar">
                                    <i className="fa-solid fa-magnifying-glass"></i>
                                </button>
                            )}
                        </div>

                        {!isMobileDevice() && (
                            <>
                                <button className="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center p-0 myDrive-header-btn"
                                    data-bs-toggle="modal" data-bs-target="#newFolderModal"
                                    onClick={() => setCreateFolderOpen(true)}
                                    title={intl.formatMessage({ id: "drive.create-folder" })}>
                                    <i className="fa-solid fa-folder-plus"></i>
                                </button>

                                <button className="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center p-0 myDrive-header-btn"
                                    data-bs-toggle="modal" data-bs-target="#recycleBinModal"
                                    title={intl.formatMessage({ id: "drive.recycle-bin" })}>
                                    <i className="fa-solid fa-trash"></i>
                                </button>
                            </>
                        )}

                        {isMobileDevice() && (
                            <button type="button" className="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center p-0 myDrive-header-btn"
                                onClick={toggleContextualMenu}>
                                <i className="fa-solid fa-bars"></i>
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {/*<nav className="col-12 mb-1 myDrive__divider" aria-label="breadcrumb">
                <ol className="breadcrumb">
                    {folderTree.map((folder, last) => {
                            return last !== (folderTree.length - 1) ?
                                <li style={{cursor: 'pointer'}} className="breadcrumb-item"
                                    onClick={e => handleGoBack(folder)} key={folder.id}>
                                    <a href="#" className={(darkMode ? "text-light" : "text-dark")}>{folder.folderName}</a>
                                </li>
                                :
                                <li key={folder.id} className="breadcrumb-item active">
                                    {folder.folderName}
                                </li>
                        }
                    )}
                </ol>
            </nav>*/}
            {folder?.files == null ?
                <Spinner />
                :
                <div className={"mb-4 " + isMobileDevice() ? "col-12" : "col-11"}>
                    <FileTable draggingAnything={draggingItems}
                        onGoBack={handleGoBack}
                        onChangeFolder={e => {
                            handleChangeFolder(e);
                        }} files={folder.files} folders={folder.folders} loading={false}
                        onOpenFile={handleOpenFile} />
                    {folder.id != 'null' && <div className="mt-3" style={{
                        textAlign: "right",
                        cursor: "pointer"
                    }} onClick={() => {
                        navigator.clipboard.writeText(folder.id);
                        toast.info("Id de la carpeta copiada al portapapeles");
                    }}>
                        <small className="text-muted">
                            {folder.id} <i className="fa-regular fa-copy ps-1"></i>
                        </small>
                    </div>}
                </div>
            }

            <ConfirmationModal open={createFolderOpen} onClose={handleCloseCreateFolder}
                title={intl.formatMessage({ id: "drive.create-folder" })}
                askInput={intl.formatMessage({ id: "drive.new-folder.name" })}
                inputType="text" btnClass="btn-success" loading={loadingNewFolder}
                modalName="newFolderModal" />

            <CustomModalV2 open={fileViewerOpen} onClose={() => {
                let basePath = location.pathname;
                if (basePath.includes("/file/")) {
                    basePath = basePath.split("/file/")[0];
                }
                navigate(basePath, { replace: true });
            }}
                title={activeFile ? activeFile.fileName : ""}>
                {activeFile &&
                    <div className="modal-body accordion accordion-flush myDriveModalContent"
                        id="fileViewerAccordion"
                        ref={fileViewerAccordion}>
                        <div className="accordion-item">
                            <div className="accordion-collapse collapse show col-12 mt-3"
                                id="collapseFileViewer"
                                data-bs-parent="#fileViewerAccordion">
                                <FileViewer file={activeFile} />
                                <div className=" collapse col-12 mt-3" id="collapseShareUrl">
                                    {activeFile.shareUrl &&
                                        <input className="form-control" type="text"
                                            style={{ cursor: "pointer" }}
                                            onClick={e => {
                                                navigator.clipboard.writeText(e.target.value);
                                                toast.info(intl.formatMessage({ id: "file-viewer.share-link-copied" }));
                                            }}
                                            value={window.location.origin + '/shared-files/' + activeFile.shareUrl}
                                            aria-label="readonly input example" readOnly />}
                                    {!activeFile.shareUrl &&
                                        <div className="d-flex flex-row justify-content-center">
                                            <Spinner />
                                        </div>}
                                </div>
                            </div>
                        </div>
                        <div className="accordion-item">
                            <div className="accordion-collapse collapse col-12 mt-3"
                                id="collapseShareFileForm"
                                data-bs-parent="#fileViewerAccordion">
                                <div className="card card-body">
                                    <ShareFileForm file={activeFile} allGroups={validGroups}
                                        onClose={() => handleShowOptions(0)} />
                                </div>
                            </div>
                        </div>
                        <div className="accordion-item">
                            <div className="accordion-collapse collapse col-12 mt-3"
                                id="collapseShareSquadflix"
                                data-bs-parent="#fileViewerAccordion">
                                <div className="card card-body">
                                    <ViewerSquadflixMenu file={activeFile}
                                        onClose={() => handleShowOptions(0)} />
                                </div>
                            </div>
                        </div>
                    </div>
                }
                <div className="mt-4 col-12 d-flex flex-row justify-content-between">
                    <div style={{ display: "none" }}>
                        <button
                            ref={buttonShowSquadflixForm}
                            type="button" className="btn btn-outline-secondary"
                            data-bs-toggle="collapse" data-bs-target="#collapseShareSquadflix"
                            aria-expanded="false" aria-controls="collapseShareSquadflix">
                        </button>
                        <button
                            ref={buttonShowFileViewer}
                            type="button" className="btn btn-outline-secondary"
                            data-bs-toggle="collapse" data-bs-target="#collapseFileViewer"
                            aria-expanded="false" aria-controls="collapseFileViewer">
                        </button>
                        <button
                            ref={buttonShareForm}
                            type="button" className="btn btn-outline-secondary"
                            data-bs-toggle="collapse" data-bs-target="#collapseShareFileForm"
                            aria-expanded="false" aria-controls="collapseShareFileForm">
                        </button>
                    </div>
                    <div>
                        <button type="button" className="btn btn-outline-secondary"
                            onClick={() => generateShareUrl(activeFile)}
                            data-bs-toggle="collapse" data-bs-target="#collapseShareUrl"
                            aria-expanded="false" aria-controls="collapseShareUrl">
                            {!isSmallScreen() && <><FormattedMessage
                                id="file-viewer.share-file" />&nbsp;</>}
                            <i className="fa-solid fa-link"></i>
                        </button>
                    </div>
                    <div>
                        <button type="button" className="btn btn-outline-secondary me-1"
                            onClick={() => addFileToQueue(activeFile)}>
                            {!isSmallScreen() && <><FormattedMessage
                                id="file-viewer.download-file" />&nbsp;</>}
                            <i className="fa-solid fa-download"></i>
                        </button>
                        <button type="button" className="btn btn-outline-secondary"
                            onClick={getValidGroups}>
                            {!isSmallScreen() && <><FormattedMessage
                                id="file-viewer.publish-file" />&nbsp;</>}
                            <i className="fa-solid fa-users"></i>
                        </button>
                        {activeFile?.fileType?.includes("video") &&
                            <button type="button" className="btn btn-outline-secondary ms-1"
                                onClick={openSquadflixForm}>
                                {!isSmallScreen() && <><FormattedMessage
                                    id="file-viewer.publish-squadflix"
                                    defaultMessage="SquadFlix" />&nbsp;</>}
                                <i className="fa-solid fa-clapperboard"></i>
                            </button>}
                    </div>
                </div>
            </CustomModalV2>
            {isMobileDevice() &&
                <div style={{ position: "fixed", zIndex: 2, bottom: 75, left: 15, height: '59px', width: '59px' }}
                    className="d-flex flex-row align-items-center">
                    <button type="button" className="btn btn-lg btn-secondary" style={{ height: '59px', width: '59px' }}
                        onClick={() => uploadUtils.current.handleAddFilesButton()}>
                        <i className="fa-solid fa-cloud-arrow-up"></i>
                    </button>
                </div>
            }
            <UploadUtils ref={uploadUtils} />
        </div>
    )
}

export default MyDrive;
